import { useEffect, useState } from 'react';
import { useInstantSearch } from 'react-instantsearch-hooks-web';
import { searchClient } from '@/algolia/searchClient';

/*
 * Bucketed facets (Pan Height, Pan Projection) are applied as an OR over the
 * individual facet values that fall inside the bucket. The refinement list
 * only exposes the values that survive the other active filters, so refining
 * from it freezes the bucket to whatever happened to be on screen at the time:
 * remove one of those other filters and the bucket stays narrow.
 *
 * This hook fetches the attribute's complete value list once, scoped only to
 * the page's base filters, so a bucket can always be applied over every value
 * in its range regardless of what else is refined.
 */
const useFacetValueUniverse = (attribute: string, enabled: boolean) => {
  const { results } = useInstantSearch();

  const state = (results as any)?._state;
  const indexName: string | undefined = state?.index;
  const filters: string = state?.filters ?? '';

  const [values, setValues] = useState<string[]>([]);

  useEffect(() => {
    if (!enabled || !indexName) return;

    let cancelled = false;

    searchClient
      .search([
        {
          indexName,
          params: {
            facets: [attribute],
            filters,
            hitsPerPage: 0,
            maxValuesPerFacet: 999,
            analytics: false,
            attributesToRetrieve: [],
            attributesToHighlight: [],
          },
        },
      ])
      .then((response) => {
        if (cancelled) return;

        const facets = (response.results[0] as any)?.facets ?? {};

        setValues(Object.keys(facets[attribute] ?? {}));
      })
      .catch(() => {
        // Fall back to the refinement list values.
      });

    return () => {
      cancelled = true;
    };
  }, [attribute, enabled, indexName, filters]);

  return values;
};

export default useFacetValueUniverse;
